import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { AppShell } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import AppHeader from './navigation/AppHeader';
import AppSidebar from './navigation/AppSidebar';
import AppSettings from '../components/settings/AppSettings';
import SchedulerSessionGuard from '../components/auth/SchedulerSessionGuard';
import SchedulerPermissionGate from '../components/auth/SchedulerPermissionGate';
import { RequestTrackingModalProvider } from '../contexts/RequestTrackingModalContext';
import { RequestTrackingModal } from '../features/request-tracking/containers/RequestTrackingModal';
import { FeatureTipsProvider } from '../features/feature-tips';
import { DashboardSidebarProvider } from '../contexts/DashboardSidebarContext';
import {
  getSidebarAutoCollapse,
  getSidebarCollapsed,
  setSidebarCollapsed,
  SIDEBAR_AUTO_COLLAPSE_CHANGED_EVENT,
} from '../navigation/sidebarPreference';

const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 56;
const HOVER_COLLAPSE_DELAY_MS = 140;

/**
 * AppLayout Component
 *
 * Shared layout: collapsible left sidebar, slim header, settings modal, main content.
 */
const AppLayout: React.FC = () => {
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(getSidebarCollapsed);
  const [sidebarAutoCollapse, setSidebarAutoCollapseState] = useState(getSidebarAutoCollapse);
  const [sidebarHoverExpanded, setSidebarHoverExpanded] = useState(false);
  const [mobileNavOpened, { toggle: toggleMobileNav, close: closeMobileNav }] = useDisclosure(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const hoverCloseTimerRef = useRef<number | null>(null);

  const handleSidebarCollapsedChange = useCallback((collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    setSidebarCollapsedState(collapsed);
  }, []);

  const clearHoverCloseTimer = useCallback(() => {
    if (hoverCloseTimerRef.current != null) {
      window.clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  }, []);

  const openSidebarHover = useCallback(() => {
    clearHoverCloseTimer();
    setSidebarHoverExpanded(true);
  }, [clearHoverCloseTimer]);

  const scheduleSidebarHoverClose = useCallback(() => {
    clearHoverCloseTimer();
    hoverCloseTimerRef.current = window.setTimeout(() => {
      setSidebarHoverExpanded(false);
      hoverCloseTimerRef.current = null;
    }, HOVER_COLLAPSE_DELAY_MS);
  }, [clearHoverCloseTimer]);

  useEffect(() => {
    return () => clearHoverCloseTimer();
  }, [clearHoverCloseTimer]);

  useEffect(() => {
    const syncAutoCollapse = () => {
      const enabled = getSidebarAutoCollapse();
      setSidebarAutoCollapseState(enabled);
      setSidebarHoverExpanded(false);
      if (enabled) {
        setSidebarCollapsed(true);
        setSidebarCollapsedState(true);
      } else {
        // Turning auto-collapse off restores a full expanded sidebar.
        setSidebarCollapsed(false);
        setSidebarCollapsedState(false);
      }
    };
    window.addEventListener(SIDEBAR_AUTO_COLLAPSE_CHANGED_EVENT, syncAutoCollapse);
    return () => window.removeEventListener(SIDEBAR_AUTO_COLLAPSE_CHANGED_EVENT, syncAutoCollapse);
  }, []);

  const handleSidebarToggle = useCallback(() => {
    if (isMobile) {
      toggleMobileNav();
      return;
    }
    if (sidebarAutoCollapse) {
      // Manual toggle exits temporary hover; keep rail collapsed while auto-collapse is on.
      setSidebarHoverExpanded(false);
      return;
    }
    handleSidebarCollapsedChange(!sidebarCollapsed);
  }, [
    isMobile,
    toggleMobileNav,
    sidebarAutoCollapse,
    handleSidebarCollapsedChange,
    sidebarCollapsed,
  ]);

  const autoCollapseActive = sidebarAutoCollapse && !isMobile;
  const shellCollapsed = autoCollapseActive || (sidebarCollapsed && !isMobile);
  const visualCollapsed = autoCollapseActive ? !sidebarHoverExpanded : shellCollapsed;
  const hoverPeek = autoCollapseActive && sidebarHoverExpanded;
  const navbarWidth = shellCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED;
  const brandColumnWidth = hoverPeek ? SIDEBAR_WIDTH_EXPANDED : navbarWidth;

  return (
    <RequestTrackingModalProvider>
      <FeatureTipsProvider>
        <DashboardSidebarProvider>
          <AppShell
            header={{ height: 60 }}
            navbar={{
              width: navbarWidth,
              breakpoint: 'sm',
              collapsed: { mobile: !mobileNavOpened, desktop: false },
            }}
            padding="0"
          >
            <AppShell.Navbar
              px={12}
              py={10}
              bg="var(--mantine-color-body)"
              onMouseEnter={() => {
                if (autoCollapseActive) openSidebarHover();
              }}
              onMouseLeave={() => {
                if (autoCollapseActive) scheduleSidebarHoverClose();
              }}
              style={{
                borderRight: '1px solid var(--mantine-color-default-border)',
                display: 'flex',
                flexDirection: 'column',
                width: hoverPeek ? SIDEBAR_WIDTH_EXPANDED : undefined,
                zIndex: hoverPeek ? 300 : undefined,
                boxShadow: hoverPeek
                  ? '8px 0 24px rgba(15, 23, 42, 0.12)'
                  : undefined,
                transition: 'width 140ms ease, box-shadow 140ms ease',
              }}
            >
              <AppSidebar
                collapsed={visualCollapsed}
                onCollapsedChange={(collapsed) => {
                  if (autoCollapseActive) {
                    // While auto-collapse is on, keep the rail collapsed; hover controls peek.
                    if (!collapsed) openSidebarHover();
                    else setSidebarHoverExpanded(false);
                    handleSidebarCollapsedChange(true);
                    return;
                  }
                  handleSidebarCollapsedChange(collapsed);
                }}
                onOpenSettings={() => {
                  setSettingsOpened(true);
                  closeMobileNav();
                }}
                onNavigate={closeMobileNav}
              />
            </AppShell.Navbar>

            <AppShell.Header p={0} bg="var(--mantine-color-body)" style={{ zIndex: hoverPeek ? 301 : undefined }}>
              <AppHeader
                sidebarCollapsed={visualCollapsed}
                navbarWidth={brandColumnWidth}
                brandPeek={hoverPeek}
                isMobile={!!isMobile}
                onSidebarToggle={handleSidebarToggle}
                mobileNavOpened={mobileNavOpened}
                onBrandMouseEnter={() => {
                  if (autoCollapseActive) openSidebarHover();
                }}
                onBrandMouseLeave={() => {
                  if (autoCollapseActive) scheduleSidebarHoverClose();
                }}
              />
            </AppShell.Header>

            <AppShell.Main
              style={{
                height: '100vh',
                overflow: 'hidden',
                backgroundColor: 'var(--app-shell-bg, var(--mantine-color-gray-1))',
              }}
            >
              <SchedulerSessionGuard>
                <SchedulerPermissionGate>
                  <Outlet />
                </SchedulerPermissionGate>
              </SchedulerSessionGuard>
            </AppShell.Main>
          </AppShell>

          <AppSettings opened={settingsOpened} onClose={() => setSettingsOpened(false)} />
          <RequestTrackingModal />
        </DashboardSidebarProvider>
      </FeatureTipsProvider>
    </RequestTrackingModalProvider>
  );
};

export default AppLayout;
